import { formatNumber } from '@/utils/string.utils';
import { Components } from '@/api/generated/api';
import type { TTranslate } from '@/interfaces';
import { GradeCalculationTagKey } from 'common/enum/grade.calculation.enum';
import { uniqSorted } from 'common/utils/array.utils';
import {
  clampPercentage,
  distributeWeightedPercentage,
  resolveEffectiveAssessments,
} from 'common/utils/grade.calculation.utils';
import { clampNumber } from 'common/utils/math.utils';

export const GRADE_CALCULATION_ACCENT_COLOR = '#5C2EE5';
export const GRADE_CALCULATION_WEIGHTING_STEP = 1;
export const GRADE_CALCULATION_WEIGHTING_TOTAL = 100;

type EffectiveWeightingContext = {
  assessments: Components.Schemas.PerformanceAssessmentDto[];
  isSubjectSpecificContext: boolean;
};

export const isDefaultGradeCalculationTag = (
  tag: Pick<Components.Schemas.GradeCalculationTagDto, 'isDefault' | 'key'> | null | undefined,
): boolean => {
  return !tag || tag.isDefault || tag.key.toLowerCase() === GradeCalculationTagKey.DEFAULT;
};

export const isExcludedGradeCalculationTag = (
  tag: Pick<Components.Schemas.GradeCalculationTagDto, 'key'> | null | undefined,
): boolean => {
  return !!tag && tag.key.toLowerCase() === GradeCalculationTagKey.EXCLUDED;
};

export const getGradeCalculationTagTranslationKey = (tagKey: string | null | undefined): string => {
  const normalizedKey = (tagKey ?? GradeCalculationTagKey.DEFAULT).toLowerCase();
  return `gradeCalculation.tags.${normalizedKey}`;
};

/** Whole values stay unpadded; fractional recommendations use the view's precision. */
export const formatGradeCalculationValue = (value: number, fractionDigits = 2): string =>
  formatNumber(value, {
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: Number.isInteger(value) ? 0 : fractionDigits,
  });

export const formatGradeCalculationTagWeightMultiplier = (multiplier: number): string =>
  `×${formatNumber(multiplier, { maximumFractionDigits: 2 })}`;

type GradeCalculationSchoolLevelTextParams = {
  enabledSchoolLevels: number[];
  availableSchoolLevels: number[];
  getSchoolLevelDisplayLabel: (level: number) => string;
  t: TTranslate;
};

const isSameSchoolLevelSet = (left: number[], right: number[]): boolean => {
  return left.length === right.length && left.every((level, index) => right[index] === level);
};

const formatSchoolLevelList = (
  schoolLevels: number[],
  getSchoolLevelDisplayLabel: (level: number) => string,
  t: TTranslate,
): string => {
  const labels = schoolLevels.map((level) => getSchoolLevelDisplayLabel(level));
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0] ?? '';
  if (labels.length === 2) {
    return t('gradeCalculation.schoolLevels.listPair', {
      firstItem: labels[0] ?? '',
      secondItem: labels[1] ?? '',
    });
  }

  const lastLabel = labels[labels.length - 1] ?? '';
  const firstLabels = labels.slice(0, -1).join(', ');
  return t('gradeCalculation.schoolLevels.listMultiple', {
    items: firstLabels,
    lastItem: lastLabel,
  });
};

export const formatGradeCalculationSchoolLevelScope = ({
  enabledSchoolLevels,
  availableSchoolLevels,
  getSchoolLevelDisplayLabel,
  t,
}: GradeCalculationSchoolLevelTextParams): string => {
  const normalizedEnabledSchoolLevels = uniqSorted(enabledSchoolLevels);
  const normalizedAvailableSchoolLevels = uniqSorted(availableSchoolLevels);
  const allSchoolLevelsSelected =
    normalizedAvailableSchoolLevels.length > 0 &&
    isSameSchoolLevelSet(normalizedEnabledSchoolLevels, normalizedAvailableSchoolLevels);

  if (allSchoolLevelsSelected) {
    return t('gradeCalculation.schoolLevels.scopeAll');
  }

  if (normalizedEnabledSchoolLevels.length === 1) {
    const [schoolLevel] = normalizedEnabledSchoolLevels;

    if (schoolLevel === 0) {
      return t('gradeCalculation.schoolLevels.scopeSinglePreSchool');
    }

    return t('gradeCalculation.schoolLevels.scopeSingle', {
      schoolLevel: getSchoolLevelDisplayLabel(schoolLevel),
    });
  }

  if (normalizedEnabledSchoolLevels.includes(0)) {
    const remainingSchoolLevels = normalizedEnabledSchoolLevels.filter((schoolLevel) => schoolLevel !== 0);

    if (remainingSchoolLevels.length === 1) {
      return t('gradeCalculation.schoolLevels.scopePreSchoolAndSingle', {
        schoolLevel: getSchoolLevelDisplayLabel(remainingSchoolLevels[0]),
      });
    }

    return t('gradeCalculation.schoolLevels.scopePreSchoolAndMultiple', {
      schoolLevels: formatSchoolLevelList(remainingSchoolLevels, getSchoolLevelDisplayLabel, t),
    });
  }

  return t('gradeCalculation.schoolLevels.scopeMultiple', {
    schoolLevels: formatSchoolLevelList(normalizedEnabledSchoolLevels, getSchoolLevelDisplayLabel, t),
  });
};

export const getEffectiveWeightingContext = ({
  allPerformanceAssessments,
  selectedSubject,
}: {
  allPerformanceAssessments: Components.Schemas.PerformanceAssessmentDto[];
  selectedSubject: Components.Schemas.SchoolSubjectDto | null;
}): EffectiveWeightingContext => {
  return resolveEffectiveAssessments(
    allPerformanceAssessments,
    selectedSubject?.id ?? null,
    selectedSubject?.useSubjectSpecificAssessments ?? false,
  );
};

export const getWeightingTotal = (
  assessments: Array<Pick<Components.Schemas.PerformanceAssessmentDto, 'weighting'>>,
): number => {
  return assessments.reduce((sum, assessment) => sum + getValidWeighting(assessment.weighting), 0);
};

export const getValidWeighting = (weighting: number | null | undefined): number => {
  return Number.isFinite(weighting) ? Math.max(0, weighting ?? 0) : 0;
};

const distributeWeightingUnitsByShares = (shares: number[], totalUnits: number): number[] => {
  if (totalUnits <= 0) return Array(shares.length).fill(0);
  return distributeWeightedPercentage(shares, totalUnits);
};

export const snapWeightingToStep = (
  value: number,
  step: number = GRADE_CALCULATION_WEIGHTING_STEP,
  total: number = GRADE_CALCULATION_WEIGHTING_TOTAL,
): number => {
  if (step <= 0) return clampPercentage(value);
  return clampNumber(Math.round(clampPercentage(value) / step) * step, 0, total);
};

export const normalizeWeightingsToStep = (
  weightings: number[],
  step: number = GRADE_CALCULATION_WEIGHTING_STEP,
  total: number = GRADE_CALCULATION_WEIGHTING_TOTAL,
): number[] => {
  const normalizedInput = weightings.map((weighting) => getValidWeighting(weighting));

  if (normalizedInput.length === 0) return [];
  if (normalizedInput.length === 1) return [total];
  if (step <= 0 || total <= 0) return normalizedInput.map((weighting) => clampPercentage(weighting));

  const inputTotal = normalizedInput.reduce((sum, w) => sum + w, 0);
  if (inputTotal <= 0) return normalizedInput;

  const totalUnits = Math.round(total / step);
  return distributeWeightingUnitsByShares(normalizedInput, totalUnits).map((units) => units * step);
};

export const withAlpha = (color: string, alpha: number): string => {
  const normalizedAlpha = clampNumber(alpha, 0, 1);
  if (!/^#([A-Fa-f0-9]{6})$/.test(color)) return color;
  const alphaHex = Math.round(normalizedAlpha * 255)
    .toString(16)
    .padStart(2, '0');
  return `${color}${alphaHex}`;
};
